import { ReactComponent as AccessArrowDownIcon } from '@/assets/icons/arrow-down.svg';
import { ReactComponent as CheckboxCheckedIcon } from '@/assets/icons/checkbox-checked.svg';
import { ReactComponent as CheckboxIcon } from '@/assets/icons/checkbox.svg';
import '@/types/mui-theme';
import { COLLAB_AVATAR_COLOR_KEYS, COLLAB_CURSOR_COLORS } from '@/utils/collaborationUserColor';
import { SvgIcon, type SvgIconProps } from '@mui/material';
import { alpha, createTheme, darken, Shadows, ThemeOptions } from '@mui/material/styles';
import { forwardRef } from 'react';
import { X } from 'lucide-react';
import { chipColorVariants } from './mui.chip';
import { POPUP_BACKDROP, POPUP_MAX_EXTENT, POPUP_RADIUS, POPUP_SCREEN_INSET } from './popup';
import { primitives, tokens } from './tokens';

const autofillStyles = {
  '&:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
    backgroundColor: '#fff !important',
  },
};

const defaultTheme = createTheme();

/** Global sort indicator for `TableSortLabel` (design: access / chevron arrow down). */
const TableSortLabelAccessIcon = forwardRef<SVGSVGElement, SvgIconProps>(
  function TableSortLabelAccessIcon(props, ref) {
    return (
      <SvgIcon
        ref={ref}
        inheritViewBox
        component={AccessArrowDownIcon}
        viewBox='0 0 16 16'
        {...props}
      />
    );
  },
);

const muiThemeOptions: ThemeOptions = {
  palette: {
    primary: {
      main: tokens.primary.main,
      light: tokens.primary.light,
      dark: tokens.primary.dark,
      contrastText: tokens.primary.contrastText,
      lighter: tokens.primary.lighter,
      darker: tokens.primary.darker,
    },
    secondary: {
      main: tokens.primary.light,
      light: tokens.primary.lighter,
      dark: tokens.primary.dark,
      contrastText: tokens.primary.contrastText,
      lighter: tokens.primary.lighter,
      darker: tokens.primary.darker,
    },
    // Figma `neutrals/border/default-border` (#EAF0F1) — the app-wide default rule.
    // This is `border.light`, one step lighter than `border.main` (#D6E0E2), which
    // stays reserved for heavier borders (input outlines, chip strokes).
    divider: tokens.border.light,
    error: {
      main: tokens.error.main,
      light: tokens.error.light,
      dark: tokens.error.dark,
      contrastText: tokens.error.contrastText,
      lighter: tokens.error.light,
      darker: tokens.error.dark,
    },
    success: {
      main: tokens.success.main,
      light: tokens.success.light,
      dark: tokens.success.dark,
      contrastText: tokens.success.contrastText,
      lighter: tokens.success.light,
      darker: tokens.success.dark,
    },
    warning: {
      main: tokens.warning.main,
      light: tokens.warning.light,
      dark: tokens.warning.dark,
      contrastText: tokens.warning.contrastText,
      lighter: tokens.warning.light,
      darker: tokens.warning.dark,
    },
    info: {
      main: tokens.info.main,
      light: tokens.info.light,
      dark: tokens.info.dark,
      lighter: tokens.info.lighter,
      darker: tokens.info.darker,
    },
    common: {
      white: tokens.common.white,
      black: tokens.common.black,
    },
    background: {
      default: tokens.background.default,
      paper: tokens.background.paper,
    },
    text: {
      primary: tokens.text.primary,
      secondary: tokens.text.secondary,
      tertiary: tokens.text.tertiary,
      disabled: tokens.text.disabled,
      placeholder: tokens.text.placeholder,
      label: tokens.text.label,
    },
    grey: {
      0: primitives.neutrals.solid[0],
      10: primitives.neutrals.solid[10],
      20: primitives.neutrals.solid[20],
      30: primitives.neutrals.solid[30],
      40: primitives.neutrals.solid[40],
      50: primitives.neutrals.solid[50],
      60: primitives.neutrals.solid[60],
      70: primitives.neutrals.solid[70],
      80: primitives.neutrals.solid[80],
      90: primitives.neutrals.solid[90],
      100: primitives.neutrals.solid[100],
      110: primitives.neutrals.solid[110],
      120: primitives.neutrals.solid[120],
      // Standard MUI keys for legacy support
      200: primitives.neutrals.solid[90],
      300: primitives.neutrals.solid[80],
      400: primitives.neutrals.solid[70],
      500: primitives.neutrals.solid[60],
      600: primitives.neutrals.solid[50],
      700: primitives.neutrals.solid[40],
      800: primitives.neutrals.solid[20],
      900: primitives.neutrals.solid[0],
    },
    green: {
      10: primitives.green[10],
      25: primitives.green[25],
      50: primitives.green[50],
      75: primitives.green[75],
      100: primitives.green[100],
      125: primitives.green[125],
    },
    // Custom extended colors (primitives)
    custom: {
      brand: {
        10: tokens.brand[10],
        25: tokens.brand[25],
        50: tokens.brand[50],
        75: tokens.brand[75],
        80: tokens.brand[80],
        100: tokens.brand[100],
        120: tokens.brand[120],
        125: tokens.brand[125],
        128: tokens.brand[128],
        150: tokens.brand[150],
        175: tokens.brand[175],
      },
      green: {
        ish700: primitives.green[25], // was green.grenish (#D0E6BE) → light green (dropped per Figma)
        700: primitives.green[100],
        800: primitives.green[125],
      },
      red: {
        red700: primitives.red[100],
        red800: primitives.red[150],
      },
      orange: {
        main: tokens.orange[100],
      },
      yellow: {
        main: primitives.yellow[100],
        yellow700: primitives.yellow[150],
      },
      // `darkBlue` and `violet` are declared NON-optional on `Palette['custom']` in
      // src/types/mui-theme.ts but were never assigned here, so `theme.palette.custom.violet.main`
      // type-checked while throwing "Cannot read properties of undefined" at runtime. Latent while
      // nothing read them; live the moment anything iterates `palette.custom` — which is exactly
      // what the Foundations colour story does. Values come from the existing ramps.
      darkBlue: {
        darkBlue100: tokens.darkBlue[100],
      },
      violet: {
        main: tokens.purple[100],
      },
      // Data-analysis Views workspace semantic group (see semantics.ts).
      dataAnalysis: tokens.dataAnalysis,
    },
  },
  shadows: [
    'none',
    '0px 5px 20px 0px rgba(0, 62, 71, 0.05)',
    '0px 8px 24px 0px rgba(0, 62, 71, 0.08)',
    '0px 12px 32px 0px rgba(0, 62, 71, 0.10)',
    '0px 16px 40px 0px rgba(0, 62, 71, 0.12)',
    '0px 20px 48px 0px rgba(0, 62, 71, 0.14)',
    '0px 24px 56px 0px rgba(0, 62, 71, 0.16)',
    ...defaultTheme.shadows.slice(7),
  ] as Shadows,
  typography: {
    fontFamily: '"Raleway", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Raleway", "Helvetica", "Arial", sans-serif',
      fontSize: '2.5rem',
      lineHeight: 1.4,
      fontWeight: 800,
    },
    h2: {
      fontSize: '2rem',
      lineHeight: 1.4,
      fontWeight: 800,
    },
    h3: {
      fontSize: '1.5rem',
      lineHeight: 1.4,
      fontWeight: 800,
    },
    h4: {
      fontSize: '1.25rem',
      lineHeight: 1.4,
      fontWeight: 800,
    },
    h5: {
      fontSize: '1.125rem',
      lineHeight: 1.4,
      fontWeight: 700,
    },
    h6: {
      fontSize: '1rem',
      lineHeight: 1.4,
      fontWeight: 700,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 'normal',
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 'normal',
      fontWeight: 400,
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 'normal',
      fontWeight: 400,
    },
  },
  shape: {
    borderRadius: 8,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  spacing: 8,
  // Custom spacing values
  customSpacing: {
    dashboardAsideWidth: 280,
    dashboardAsideWidthCollapsed: 72,
  },
  components: {
    MuiCssBaseline: {
      // Global base styles. Consolidated here from the legacy src/main.css +
      // src/styles/global.css (which raced these by import order). Component-scoped
      // rules from those files still live with their components (app-shell wrappers,
      // the .loader spinner, empty-state, landing wrapper) pending migration.
      styleOverrides: {
        'html, body, #root': {
          // Full-height app shell. CssBaseline already applies box-sizing: border-box.
          height: '100%',
        },
        body: {
          fontFamily: '"Raleway", "Helvetica", "Arial", sans-serif',
          fontVariantNumeric: 'lining-nums proportional-nums',
          backgroundColor: tokens.background.default,
          overflowX: 'hidden',
        },
        'h1, h2, h3, h4, h5, h6, figcaption': {
          margin: 0,
        },
        'ul, ol': {
          paddingLeft: '1rem',
        },
        // ⚠ Known a11y debt: global focus-outline removal, carried over verbatim from
        // main.css (surfaced by the report-only axe pass in `npm run test-storybook:ci`).
        // Revisit separately.
        'input:focus, textarea:focus, select:focus, div:focus': {
          outline: 'none',
        },
        // Raw <input>/<textarea> don't inherit body's font-family — set it explicitly.
        'input, textarea': {
          fontFamily: '"Raleway", sans-serif',
          fontSize: 16,
          lineHeight: '24px',
        },
        code: {
          fontFamily: "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace",
        },
        // Native <select> placeholder tint (empty option not yet chosen).
        ":has(option[value='']:not(:checked))": {
          color: tokens.primary.darker,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '16px 24px',
        },
        outlinedInfo: {
          backgroundColor: tokens.primary.lighter,
          border: `1px solid ${tokens.primary.main}`,
        },
      },
      variants: [
        {
          props: { variant: 'filled', color: 'info' },
          style: {
            color: '#fff',
          },
        },
      ],
    },
    MuiAvatarGroup: {
      defaultProps: {
        slotProps: {
          surplus: {
            sx: {
              fontSize: 12,
              fontWeight: 600,
              color: tokens.primary.darker,
              background: '#fff',
              border: `1px dashed ${alpha(tokens.primary.darker, 0.2)}`,
            },
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          width: 24,
          height: 24,
          fontSize: 12,
          fontWeight: 700,
          color: '#fff',
          backgroundColor: tokens.primary.dark,
        },
      },
      variants: [
        {
          props: { color: 'inactive' },
          style: {
            color: alpha(tokens.primary.darker, 0.6),
            background: '#fff',
            border: `1px dashed ${alpha(tokens.primary.darker, 0.2)}`,
          },
        },
        ...COLLAB_CURSOR_COLORS.map((color, idx) => ({
          props: { color: COLLAB_AVATAR_COLOR_KEYS[idx] },
          style: {
            color: '#fff',
            backgroundColor: color,
          },
        })),
      ],
    },
    MuiAutocomplete: {
      styleOverrides: {
        listbox: {
          padding: 8,
          '& .MuiAutocomplete-option[aria-selected="true"]': {
            backgroundColor: 'transparent',
            '&.Mui-focused': {
              backgroundColor: tokens.grey[120],
            },
          },
        },
        option: {
          padding: '6px 12px',
          fontSize: 12,
          fontWeight: 700,
          borderRadius: 4,
          color: tokens.text.tertiary,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: tokens.grey[120],
          },
        },
        inputRoot: {
          paddingBlock: 3,
        },
      },
    },
    MuiChip: {
      defaultProps: {
        deleteIcon: <X strokeWidth={3.5} size={12} />,
      },
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          fontWeight: 700,
        },
        deleteIcon: {
          color: alpha(tokens.primary.darker, 0.5),
          marginLeft: 0,
          marginRight: 8,
        },
        icon: {
          flexShrink: 0,
          marginLeft: 2,
        },
      },
      variants: [
        {
          props: { variant: 'pill' },
          style: {
            borderRadius: 9999,
            '& .MuiChip-label': {
              paddingInline: '12px',
            },
          },
        },
        {
          props: { variant: 'pill', size: 'small' },
          style: {
            '& .MuiChip-label': {
              paddingInline: '10px',
            },
          },
        },
        ...chipColorVariants,
        {
          props: { variant: 'action' },
          style: {
            borderRadius: 4,
          },
        },
      ],
    },
    MuiCircularProgress: {
      styleOverrides: {
        track: {
          color: tokens.grey[80],
          opacity: 1,
        },
      },
    },
    /**
     * The current global pop-up shell — every dialog in the app, `Modal` or raw `Dialog`,
     * gets it. The numbers and the Figma source live in `./popup`; sizes are `<Modal size>`.
     * MUI's own 32px edge gap and 64px height cap are replaced by the 40px inset, and a
     * full-screen dialog keeps MUI's edge-to-edge paper.
     */
    MuiDialog: {
      styleOverrides: {
        root: {
          '& .MuiBackdrop-root': {
            backgroundColor: POPUP_BACKDROP,
          },
        },
        paper: ({ ownerState, theme }) =>
          ownerState.fullScreen
            ? {}
            : {
                margin: POPUP_SCREEN_INSET,
                borderRadius: POPUP_RADIUS,
                boxShadow: theme.shadows[1],
                ...(ownerState.scroll !== 'body' && { maxHeight: POPUP_MAX_EXTENT }),
                ...(!ownerState.maxWidth && { maxWidth: POPUP_MAX_EXTENT }),
                ...(ownerState.fullWidth && { width: POPUP_MAX_EXTENT }),
              },
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          minHeight: 36,
          padding: '8px 16px',
          fontWeight: 700,
          lineHeight: 1.4,
          boxShadow: 'none',
          fontSize: '0.875rem',
          textTransform: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
          // Pressed feedback: the ripple is disabled, so darken slightly on press. Universal across
          // every variant/colour (contained darkens the fill; outlined/text darken text + border).
          '&:active': {
            filter: 'brightness(0.92)',
          },
        },
        startIcon: {
          marginLeft: 0,
        },
        endIcon: {
          // Inherit the button's colour so an end icon matches the start icon (which already
          // inherits). Dropdown-style buttons that want a muted end chevron set the icon colour
          // explicitly at the call site (SelectCountryAutocomplete, RequestTemplateBlock).
          color: 'inherit',
        },
        contained: {
          '&.Mui-disabled': {
            backgroundColor: tokens.grey[70],
            color: '#fff',
            boxShadow: 'none',
            opacity: 1,
          },
          '&.MuiButton-containedGray': {
            color: tokens.grey[0],
            backgroundColor: tokens.grey[90],
            '&:hover': {
              backgroundColor: tokens.grey[80],
            },
            // Figma's disabled gray (#779): this rule is as specific as the shared disabled
            // one above and comes later, so without its own disabled look a disabled gray
            // button painted exactly like an enabled one. Last, so hover cannot repaint it.
            '&.Mui-disabled': {
              color: tokens.text.disabled,
              backgroundColor: tokens.grey[120],
            },
          },
        },
        containedSecondary: {
          // Contained + secondary: brand-150 text on a brand-50 background,
          // brightening to brand-25 on hover.
          color: tokens.brand[150],
          backgroundColor: tokens.brand[50],
          '&:hover': {
            backgroundColor: tokens.brand[25],
          },
        },
        // Info and Warning set their contained hover explicitly: MUI's default would use their
        // `.dark` steps (#055E68 / #452000 after the Figma migration), which are near-black — far
        // too dark as a hover fill. A gentle darken of the rest colour reads as a natural hover and
        // keeps the (dark, for warning) label legible.
        containedInfo: {
          '&:hover': {
            backgroundColor: darken(tokens.info.main, 0.12),
          },
        },
        containedWarning: {
          '&:hover': {
            backgroundColor: darken(tokens.warning.main, 0.12),
          },
        },
        outlined: {
          // paddingBlock: 6,
          // Figma `buttons style=gray, level=outlined (sc)`: the secondary action, e.g. Cancel.
          '&.MuiButton-outlinedGray': {
            color: tokens.grey[0],
            backgroundColor: tokens.grey[120],
            borderColor: tokens.border.light,
            '&:hover': {
              // The drawn hover fill (Figma variable `37762:430258`) has no palette step.
              backgroundColor: '#F0F0F4',
              borderColor: tokens.border.dark,
            },
            '&.Mui-disabled': {
              opacity: 1,
              color: tokens.text.disabled,
              borderColor: tokens.border.light,
              '& .MuiButton-startIcon': {
                color: tokens.text.disabled,
              },
            },
            '& .MuiButton-startIcon': {
              color: tokens.grey[20],
            },
          },
        },
        outlinedError: {
          borderColor: primitives.red[100],
          '&:hover': {
            borderColor: 'transparent',
            backgroundColor: alpha(primitives.red[100], 0.2),
          },
        },
        sizeLarge: {
          minHeight: 48,
          padding: '12px 40px',
          fontSize: '0.875rem',
          borderRadius: 12,
        },
      },
      variants: [
        {
          props: { color: 'primary', variant: 'text' },
          style: {
            color: tokens.brand[120],
            '&:hover': {
              color: tokens.brand[125],
              backgroundColor: 'transparent',
            },
          },
        },
        {
          props: { color: 'success' },
          style: {
            startIcon: {
              color: 'inherit',
            },
          },
        },
        {
          // Figma `buttons style=primary, level=outlined (sc)`. The hairline is an inset
          // shadow so it adds no height at any button size.
          props: { color: 'primaryLight' },
          style: {
            color: tokens.brand[150],
            backgroundColor: primitives.brand[10],
            boxShadow: `inset 0 0 0 1px ${tokens.brand[75]}`,
            '&:hover': {
              backgroundColor: tokens.primary.lighter,
              boxShadow: `inset 0 0 0 1px ${tokens.brand[75]}`,
            },
          },
        },
        {
          props: { color: 'errorLight' },
          style: {
            color: primitives.red[100],
            backgroundColor: alpha(primitives.red[100], 0.1),
            '&:hover': {
              backgroundColor: alpha(primitives.red[100], 0.2),
            },
          },
        },
      ],
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: 4,
          color: tokens.grey[50],
          '&:hover': {
            color: tokens.grey[40],
            backgroundColor: tokens.grey[90],
          },
          // The size variants below fix the box, so the glyph must never be stretched or
          // squeezed to fit it — it stays at its own px size, optically centred.
          '& > svg': {
            flexShrink: 0,
          },
          '&.MuiIconButton-colorGray': {
            color: tokens.primary.darker,
            backgroundColor: tokens.grey[90],
            transition: 'all 0.2s ease-in-out',

            '&:hover': {
              backgroundColor: tokens.grey[100],
              boxShadow: `inset 0 0 0 1px ${tokens.grey[50]}`,
            },
          },
        },
      },
      variants: [
        {
          props: { size: 'small' },
          style: {
            minWidth: 24,
            minHeight: 24,
          },
        },
        {
          props: { size: 'medium' },
          style: {
            minWidth: 32,
            minHeight: 32,
          },
        },
        {
          props: { size: 'large' },
          style: {
            minWidth: 40,
            minHeight: 40,
          },
        },
        {
          props: { color: 'primary' },
          style: {
            color: '#fff',
            backgroundColor: tokens.primary.main,
            '&:hover': {
              color: '#fff',
              backgroundColor: tokens.primary.dark,
            },
          },
        },
        {
          props: { color: 'primaryLight' },
          style: {
            color: tokens.primary.dark,
            backgroundColor: tokens.primary.lighter,
            '&:hover': {
              color: '#fff',
              backgroundColor: tokens.primary.light,
            },
          },
        },
        {
          props: { color: 'error' },
          style: {
            color: tokens.error.main,
            '&:hover': {
              color: tokens.error.main,
              backgroundColor: tokens.error.light,
            },
          },
        },
        {
          // Figma's ghost success button. Without it `root`'s grey ink wins over MUI's own
          // `colorSuccess`. The hover keeps `root`'s grey wash: Figma draws no success hover.
          props: { color: 'success' },
          style: {
            color: tokens.success.main,
            '&:hover': {
              color: tokens.success.main,
            },
          },
        },
      ],
    },
    MuiInput: {
      styleOverrides: {
        input: {
          padding: '11px 14px',
        },
        inputSizeSmall: {
          paddingTop: 8,
          paddingBottom: 8,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          fontWeight: 500,
          color: primitives.neutrals.solid[0],
          '&.MuiInput-underline:before': {
            borderBottom: `1px solid ${tokens.border.main}`,
          },
          '&.MuiInput-underline:hover:not(.Mui-disabled, .Mui-error):before': {
            borderBottom: `1px solid ${tokens.border.main}`,
          },
        },
        input: {
          // match the blinking caret to the focused border color
          caretColor: tokens.primary.main,
          '&::placeholder': {
            opacity: 1,
            color: alpha(tokens.text.secondary, 0.5),
            fontSize: '0.875rem',
            fontWeight: 500,
          },
          '&:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
            backgroundColor: '#fff !important',
          },
          '&:-webkit-autofill:hover': {
            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
            backgroundColor: '#fff !important',
          },
          '&:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
            backgroundColor: '#fff !important',
          },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: tokens.grey[50],
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        icon: <SvgIcon color='inherit' component={CheckboxIcon} />,
        checkedIcon: <SvgIcon color='inherit' component={CheckboxCheckedIcon} />,
      },
      styleOverrides: {
        root: {
          padding: 11,
          color: alpha(tokens.primary.darker, 0.8),

          '&.Mui-checked': {
            color: alpha(tokens.primary.dark, 0.8),
          },
        },
      },
      variants: [
        {
          props: { size: 'small' },
          style: {
            '& .MuiSvgIcon-root': {
              fontSize: 14,
            },
          },
        },
      ],
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: alpha(tokens.primary.darker, 0.3),
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: '0.75rem',
          fontWeight: 700,
          color: alpha(tokens.primary.darker, 0.8),
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontWeight: 700,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          lineHeight: 1,
          fontSize: '0.75rem',
          fontWeight: 700,
          color: tokens.text.secondary,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        /**
         * Re-centre the shrunk outlined label on the border.
         *
         * A `<fieldset>` draws its top border through the LEGEND's vertical centre, not at its own
         * top edge. MUI fixes `legend { height: 11px }` (NotchedOutline.js), so the border always
         * lands at `inputRoot.top + 5.5px`. It then floats the label with a hard-coded
         * `translate(14px, -9px) scale(0.75)` (InputLabel.js) and `transform-origin: top left` —
         * numbers calibrated for MUI's own label box, which is ~23px tall (16px × lineHeight
         * 1.4375) and therefore ~17px after the 0.75 scale.
         *
         * `MuiFormLabel.root` above overrides that box to 12px with `lineHeight: 1`, i.e. 9px after
         * scaling. Since the transform scales from the TOP-left, a shorter box keeps its top edge
         * and loses height off the BOTTOM, dragging its centre upward:
         *     centre = inputRoot.top − 9 + (9 / 2) = inputRoot.top − 4.5
         * against a border at inputRoot.top + 5.5 — the label rode ~5px high on every focused or
         * filled outlined field, app-wide.
         *
         * −9 + 5 = −4 puts the centre back on the border: inputRoot.top − 4 + 4.5 = +0.5 ≈ 5.5.
         * MUI uses this one rule for both `medium` and `small`, and our label height does not vary
         * by size, so a single override covers both.
         *
         * COUPLED TO `MuiFormLabel.root.fontSize`/`lineHeight` — change either and this must be
         * recomputed as `-9 + (17.25 − labelHeight) / 2`. Verified against Components/Forms/Text field.
         */
        outlined: {
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -4px) scale(0.75)',
          },
        },
      },
    },
    MuiSwitch: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          '&:hover .MuiSwitch-track': {
            backgroundColor: '#7f9799',
          },
        },
        switchBase: {
          color: '#99B2B5',
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '&.Mui-checked': {
            color: tokens.primary.main,
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '&+.MuiSwitch-track': {
              backgroundColor: alpha(tokens.primary.darker, 0.5),
            },
          },
          '&.Mui-disabled': {
            opacity: 0.6,
          },
        },
        track: {
          backgroundColor: '#99B2B5',
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 4,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 4,
          borderRadius: 4,
          backgroundColor: alpha(tokens.primary.darker, 0.1),
        },
      },
    },
    /**
     * The dropdown surface. Figma `35744:78928` (country select) is the spec: an 8px-padded
     * 12px-radius card whose rows are 36px tall. `MuiPopover.paper` below already supplies the
     * card (radius/border/shadow) because MUI's `Menu` renders through `Popover`; only the list
     * padding is Menu-specific.
     *
     * `MuiList.root` sets `padding: 4` for every list in the app, and both rules land on the SAME
     * element (a MenuList carries `.MuiList-root` and `.MuiMenu-list`). Rather than depend on
     * theme-key ordering, this wins on specificity.
     */
    MuiMenu: {
      styleOverrides: {
        list: {
          '&.MuiList-root': {
            padding: 8,
          },
        },
      },
    },
    /**
     * One option row, per Figma `35744:78928` → `modal-btn`: 36px tall, 8px/12px padding, 4px
     * radius, an 8px gap to any leading icon, and `body2/semibold` (12px / 600 / 1.4) in
     * `text/secondary`. Kept deliberately identical to `PopoverMenu`'s `ListRow`, which drops its
     * own `gap` to inherit from here — the two menus are meant to be indistinguishable.
     */
    MuiMenuItem: {
      styleOverrides: {
        root: {
          gap: 8,
          padding: '8px 12px',
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.4,
          borderRadius: 4,
          color: tokens.text.secondary,
          '&.MuiButtonBase-root': {
            minHeight: 36,
            '&:hover': {
              backgroundColor: tokens.grey[120],
            },
          },
          // Selected stays in the neutral family instead of MUI's primary-tinted `action.selected`
          // (a teal wash reads as a brand accent the design never puts on a row), but one step
          // darker than the `grey[120]` hover so the current option is still tellable from the row
          // the pointer happens to be over. Hovering the selected row keeps the darker fill.
          '&.Mui-selected': {
            backgroundColor: tokens.grey[100],
            '&:hover, &.Mui-focusVisible': {
              backgroundColor: tokens.grey[100],
            },
          },
          // 24px keeps icons in one alignment column (the Figma leading asset is a 24px flag)
          // while the row's own 8px gap — not a wide min-width — sets the distance to the label.
          '& .MuiListItemIcon-root': {
            minWidth: 24,
          },
        },
      },
      variants: [
        {
          props: { divider: true },
          style: {
            padding: 0,
          },
        },
      ],
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          // Icons stay a neutral gray by default, independent of the item's text
          // color (e.g. a danger item's red label keeps a gray icon).
          color: tokens.text.secondary,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          '& .MuiTypography-root': {
            fontSize: 12,
            fontWeight: 600,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover': {
            '&:not(.Mui-disabled, .Mui-readOnly, .Mui-error)': {
              backgroundColor: tokens.grey[120],
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: tokens.border.main,
              },
            },
          },
          '&.Mui-focused:not(.Mui-readOnly)': {
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.primary.main,
              borderWidth: 1,
              boxShadow: `0 0 0 1px ${tokens.primary.lighter}`,
            },
          },
          '&.Mui-disabled': {
            // One step lighter on the neutral-solid ramp (was 90 / #D6E0E2). At 90 a
            // disabled field read as darker than the read-only one right next to it
            // (`Mui-readOnly` is grey[120]), which inverted the meaning: the more
            // inert control looked heavier. 100 sits between the two.
            backgroundColor: tokens.grey[100],
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
            },
          },
          '&.Mui-readOnly': {
            backgroundColor: tokens.grey[120],
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
            },
          },
          '&.Mui-error': {
            backgroundColor: tokens.error.light,
          },
        },
        input: {
          padding: '11px 14px',
          height: 20,
          '&::placeholder': {
            fontSize: '0.875rem',
            fontWeight: 500,
            color: alpha(tokens.text.secondary, 0.5),
          },
          '&:-webkit-autofill': {
            ...autofillStyles,
          },
          '&:-webkit-autofill:hover': {
            ...autofillStyles,
          },
          '&:-webkit-autofill:focus': {
            ...autofillStyles,
          },
          '&:hover:-webkit-autofill': {
            ...autofillStyles,
          },
        },
        inputMultiline: {
          padding: 0,
          lineHeight: 1.5,
        },
        sizeSmall: {
          input: {
            height: 19,
            padding: '9.5px 14px',
          },
        },
        /**
         * `notchedOutline` is the `<fieldset>`; its `<legend>` is the gap the floating label sits
         * in. MUI sizes that gap by rendering the label text into the legend at `fontSize: 0.75em`
         * (NotchedOutline.js) — `em` resolving against THIS element's font size — and shrinks the
         * real label with `transform: scale(0.75)` (InputLabel.js).
         *
         * That only lines up while the label and the input share a font size, which MUI assumes.
         * This theme breaks the assumption: `MuiFormLabel.root` sets the label to `0.75rem` (12px)
         * while `MuiInputBase.root` keeps the input at `0.875rem` (14px). So the legend inherited
         * 14px and reserved 0.75 × 14 = 10.5px of gap, while the label actually rendered at
         * 12 × 0.75 = 9px — a notch ~21px wider than its label, i.e. a visible break in the border
         * to the right of the text on every focused/filled outlined field with a label.
         *
         * Pinning the fieldset to the LABEL's size makes MUI's own `0.75em` resolve to 9px and the
         * gap match. Keep this equal to `MuiFormLabel.root.fontSize` — if that changes, change
         * this with it. Verified against Components/Forms/Text field.
         */
        notchedOutline: {
          borderColor: tokens.border.main,
          fontSize: '0.75rem',
        },
      },
    },
    MuiPopper: {
      styleOverrides: {
        root: {
          '& .MuiPaper-root': {
            marginTop: 4,
            borderRadius: 12,
            border: `1px solid ${tokens.border.light}`,
          },
        },
      },
    },
    /**
     * The floating card behind every menu, select dropdown and popover. Figma `35744:78928`:
     * 12px radius, a 1px `--border` (#EAF0F1 = `border.light`, i.e. `divider` — NOT the darker
     * `border.main` #D6E0E2 this used to draw) and `overlay-shadows/floating-card`, which is
     * exactly `shadows[1]`, hence `elevation: 1`.
     */
    MuiPopover: {
      defaultProps: {
        elevation: 1,
      },
      styleOverrides: {
        paper: {
          marginTop: 4,
          borderRadius: 12,
          border: `1px solid ${tokens.border.light}`,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize: '0.75rem',
          fontWeight: 700,
          minHeight: 20,
          height: 20,
          paddingBlock: 10,
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          fontWeight: 600,
          color: tokens.primary.darker,
          backgroundColor: tokens.grey[120],
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 28,
        },
        list: {
          position: 'relative',
          zIndex: 1,
          gap: 4,
          padding: 4,
          borderRadius: 8,
          border: `1px solid ${alpha(tokens.primary.darker, 0.2)}`,
        },
        indicator: {
          top: 4,
          bottom: 4,
          height: 'calc(100% - 8px)',
          backgroundColor: '#fff',
          borderRadius: 4,
          boxShadow: '0px 1px 3px 1px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiTab: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          fontWeight: 700,
          padding: '.375rem .75rem',
          color: alpha(tokens.primary.darker, 0.8),
          borderRadius: 4,
          minWidth: 58,
          minHeight: 28,
          backgroundColor: tokens.grey[100],
          '&.Mui-selected': {
            color: tokens.primary.darker,
            backgroundColor: '#fff',
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate',
        },
        stickyHeader: {
          borderRadius: 8,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '0.25rem 1rem',
          fontWeight: 600,
          borderColor: tokens.border.main,
          fontSize: 12,
        },
        // Figma `th` (body2/semibold): text-tertiary #6B757B. Size/weight (12/600)
        // come from `root`, so a real MUI `<thead>` matches the design by default.
        head: {
          color: tokens.text.tertiary,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover': {
            '&:hover': {
              backgroundColor: alpha(tokens.grey[100], 0.5),
            },
          },
        },
      },
    },
    MuiTableSortLabel: {
      defaultProps: {
        IconComponent: TableSortLabelAccessIcon,
      },
      styleOverrides: {
        icon: {
          fontSize: 16,
          width: 16,
          height: 16,
          marginLeft: 4,
          color: tokens.grey[50],
        },
        root: {
          '&.Mui-active .MuiTableSortLabel-icon': {
            color: tokens.primary.main,
          },
        },
      },
    },
    MuiTextField: {
      variants: [
        {
          props: { variant: 'standard', size: 'small' },
          style: {},
        },
      ],
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.75rem',
          fontWeight: 500,
          backgroundColor: tokens.text.primary,
          color: '#fff',
          boxShadow: `0 1px 12px 0 ${alpha(tokens.text.primary, 0.1)}`,
          borderRadius: 4,
        },
        arrow: {
          color: tokens.text.primary,
        },
      },
    },
  },
};

export const muiTheme = createTheme(muiThemeOptions);
